import { api } from '@/lib/api';
import { fromMinorUnits } from '@/lib/money';
import type { ConferenceHall, EventService } from '@/types';

// ─── BACKEND RESPONSE SHAPES ──────────────────────────────────────────
interface BackendConferenceHall {
  id: string;
  name: string;
  capacity: number;
  description: string;
  baseRateMinorUnits: number;
  currency: string;
  amenities: string[];
  images: string[];
  imageKeys?: string[];
  sizeSqm: number;
  location: string;
  isActive: boolean;
}

interface BackendEventService {
  id: string;
  name: string;
  category: EventService['category'];
  priceMinorUnits: number;
  currency: string;
  unit: EventService['unit'];
  description: string;
  isRequired: boolean;
  isActive: boolean;
}

// ─── MAPPERS ──────────────────────────────────────────────────────────
function mapConferenceHall(h: BackendConferenceHall): ConferenceHall {
  return {
    id: h.id,
    name: h.name,
    capacity: h.capacity ?? 0,
    description: h.description ?? '',
    baseRate: fromMinorUnits(h.baseRateMinorUnits),
    amenities: h.amenities ?? [],
    images: h.images ?? [],
    imageKeys: h.imageKeys ?? [],
    size: h.sizeSqm ?? 0,
    location: h.location ?? '',
  };
}

function mapEventService(s: BackendEventService): EventService {
  return {
    id: s.id,
    name: s.name,
    category: s.category,
    price: fromMinorUnits(s.priceMinorUnits),
    unit: s.unit,
    description: s.description ?? '',
    isRequired: !!s.isRequired,
  };
}

/**
 * The events catalog lives in the backend now (public endpoints), so the halls
 * and services pages send real UUIDs the API can price — not the old mock slugs.
 * A short in-memory cache keeps the pages snappy across a session.
 */
let hallsCache: ConferenceHall[] | null = null;
let servicesCache: EventService[] | null = null;

export const eventsCatalogService = {
  /**
   * Fetch all conference halls from /conference-halls. Throws on failure so the
   * page can toast — an empty catalog must never be shown as "no halls".
   */
  async getConferenceHalls(force = false): Promise<ConferenceHall[]> {
    if (hallsCache && !force) return hallsCache;
    const data = await api.get<BackendConferenceHall[]>('/conference-halls', { auth: false });
    hallsCache = (data || []).map(mapConferenceHall);
    return hallsCache;
  },

  /**
   * Fetch all event services from /event-services. Throws on failure so the
   * page can toast.
   */
  async getEventServices(force = false): Promise<EventService[]> {
    if (servicesCache && !force) return servicesCache;
    const data = await api.get<BackendEventService[]>('/event-services', { auth: false });
    servicesCache = (data || []).map(mapEventService);
    return servicesCache;
  },

  clearCache(): void {
    hallsCache = null;
    servicesCache = null;
  },
};

export default eventsCatalogService;
